import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useMetro } from '../context/MetroContext'
import {
  getAllTouristPlaces,
  formatDistance,
  hasCityNearbyData,
  calculateDistance,
} from '../services/nearbyPlaces'
import { NearbyPlace, Station } from '../types/metro.types'
import { BannerAd, AD_SLOTS } from '../components/ads'

interface NearbyStation {
  station: Station;
  distanceMeters: number;
}

function TouristPlaces() {
  const { selectedCity, currentCity, stations, isLoading } = useMetro()
  const [selectedPlaceId, setSelectedPlaceId] = useState('')

  // Get all tourist places for the city
  const touristPlaces = useMemo((): NearbyPlace[] => {
    if (!selectedCity || !hasCityNearbyData(selectedCity)) {
      return []
    }
    return getAllTouristPlaces(selectedCity)
  }, [selectedCity])

  // Get selected place
  const selectedPlace = useMemo(() => {
    return touristPlaces.find(p => p.id === selectedPlaceId)
  }, [touristPlaces, selectedPlaceId])

  // Get stations with coordinates
  const stationsWithCoords = useMemo(() => {
    return stations.filter(s => s.coordinates?.latitude && s.coordinates?.longitude)
  }, [stations])

  // Get nearby stations for selected place
  const nearbyStations = useMemo((): NearbyStation[] => {
    if (!selectedPlace || stationsWithCoords.length === 0) {
      return []
    }

    return stationsWithCoords
      .map(station => ({
        station,
        distanceMeters: calculateDistance(
          selectedPlace.latitude,
          selectedPlace.longitude,
          station.coordinates!.latitude,
          station.coordinates!.longitude
        ),
      }))
      .sort((a, b) => a.distanceMeters - b.distanceMeters)
      .slice(0, 5)
  }, [selectedPlace, stationsWithCoords])

  // Check if city has nearby data
  const hasData = selectedCity && hasCityNearbyData(selectedCity)

  if (!selectedCity) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive">
          <div className="card text-center py-12">
            <span className="text-6xl mb-4 block">🏙️</span>
            <h2 className="text-xl font-semibold mb-2">Select a City First</h2>
            <p className="text-neutral-dark/70 mb-4">
              Please select a city to explore tourist places near metro stations
            </p>
            <Link to="/cities" className="btn-primary inline-block">
              Select City
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive">
          <div className="card text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-metro-blue border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-neutral-dark/70">Loading city data...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-8 lg:py-12">
      <div className="container-responsive">
        <h1 className="text-3xl lg:text-4xl 2xl:text-tv-2xl font-bold text-center mb-4">
          Tourist Places
        </h1>
        <p className="text-center text-neutral-dark/70 mb-8">
          Discover tourist attractions near metro stations in {currentCity?.name}
        </p>

        {!hasData && (
          <div className="card text-center py-8 mb-8 bg-metro-yellow-soft">
            <span className="text-4xl mb-2 block">⚠️</span>
            <p className="text-neutral-dark/70">
              Tourist places data is not yet available for {currentCity?.name}.
              <br />Data available for: Delhi, Mumbai, Bangalore, Chennai, Kolkata, Hyderabad, Kochi, Lucknow, Jaipur, Nagpur, Ahmedabad
            </p>
          </div>
        )}

        {hasData && (
          <>
            {/* Place Selector */}
            <div className="card mb-8 max-w-xl mx-auto">
              <label className="block text-sm font-medium mb-2">Select Tourist Place</label>
              <select
                value={selectedPlaceId}
                onChange={(e) => setSelectedPlaceId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-metro-blue focus:border-transparent"
              >
                <option value="">Choose a place to find nearest stations...</option>
                {touristPlaces.map((place) => (
                  <option key={place.id} value={place.id}>
                    {place.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Nearby Stations */}
            {selectedPlace ? (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-center mb-4">
                  Nearest Metro Stations to {selectedPlace.name}
                </h2>
                {nearbyStations.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4 max-w-5xl mx-auto">
                    {nearbyStations.map(({ station, distanceMeters }, index) => (
                      <div key={station.id} className={`card ${index === 0 ? 'ring-2 ring-metro-green bg-metro-green-soft/30' : ''}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                            index === 0 ? 'bg-metro-green' : 'bg-gray-400'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">{station.name}</h3>
                          </div>
                        </div>
                        <p className="text-xs text-neutral-dark/60 mb-2">
                          {station.lines.join(', ')}
                        </p>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-metro-blue font-medium">
                            📍 {formatDistance(distanceMeters)}
                          </span>
                          <span className="text-neutral-dark/50">
                            ~{Math.ceil(distanceMeters / 80)} min walk
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="card text-center py-8 text-neutral-dark/50 max-w-xl mx-auto">
                    <p>No stations with location data available</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="card text-center py-12 text-neutral-dark/50 mb-8">
                <span className="text-6xl mb-4 block">🚇</span>
                <p className="text-lg">Select a tourist place to see nearby metro stations</p>
              </div>
            )}

            {/* All Tourist Places Grid */}
            <h2 className="text-xl font-semibold mb-4">All Tourist Places in {currentCity?.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
              {touristPlaces.map((place, index) => (
                <>
                  <div key={place.id} className="card">
                    <div className="aspect-video bg-metro-blue-soft rounded-lg mb-4 overflow-hidden">
                      {place.imageUrl ? (
                        <img
                          src={place.imageUrl}
                          alt={place.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-4xl">🏛️</span>
                        </div>
                      )}
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{place.name}</h3>
                    {place.address && (
                      <p className="text-sm text-neutral-dark/70 mb-3">
                        {place.address}
                      </p>
                    )}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setSelectedPlaceId(place.id)}
                        className="text-sm text-metro-blue hover:underline"
                      >
                        Find nearest stations
                      </button>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${place.latitude},${place.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-metro-blue hover:underline flex items-center gap-1"
                      >
                        🗺️ Maps
                      </a>
                    </div>
                  </div>
                  {/* In-feed Ad - Every 4th card */}
                  {(index + 1) % 4 === 0 && index < touristPlaces.length - 1 && (
                    <BannerAd
                      key={`ad-${index}`}
                      slot={AD_SLOTS.TOURIST_FEED}
                      format="rectangle"
                      wrapperClassName="card flex items-center justify-center bg-gray-50"
                    />
                  )}
                </>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default TouristPlaces
